import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import {
    getContact,
    getFooter,
    getHome,
    getPostural,
    getTrainer,
    getManagement,
    getCertification,
    getTeam,
    getGallery,
    getNavigation,
} from "../services";
import Loading from "../components/Layout/Loading";

const textForWA = "Hallo, Saya ingin bertanya mengenai I Fit Sport Indonesia";

export default function Main() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [showNumber, setShowNumber] = useState(false);
    const [contact, setContact] = useState([]);
    const [footer, setFooter] = useState([]);
    const location = useLocation();

    const rawWaNumber = footer[3]?.value || "6282125476557";
    const waNumber = rawWaNumber.replace(/\D/g, "") || "6282125476557";
    const formattedNumber = rawWaNumber.replace(/^62/, "0").replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3");

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // Route-aware document title (preserves the root "I FITS INDONESIA" verbatim while optimizing SEO per page)
    useEffect(() => {
        const titles = {
            "/": "I FITS INDONESIA",
            "/postural": "Postural Correction & Spine Specialist | I FITS INDONESIA",
            "/personaltrainer": "Personal Trainer & Exercise Therapy | I FITS INDONESIA",
            "/gymmanagement": "Gym Management & Fitness Consulting | I FITS INDONESIA",
            "/certification": "Pelatihan & Sertifikasi Trainer | I FITS INDONESIA",
            "/team": "Our Professional Coaches & Team | I FITS INDONESIA",
            "/gallery": "Galeri & Aktivitas Studio | I FITS INDONESIA",
            "/contact": "Hubungi Kami & Lokasi Studio | I FITS INDONESIA",
        };
        document.title = titles[location.pathname] || "I FITS INDONESIA";
    }, [location.pathname]);

    useEffect(() => {
        try {
            sessionStorage.removeItem("ifit_site_loaded");
        } catch {}

        const fetchInitialData = async () => {
            try {
                // Prefetch all website data concurrently during initial loading screen
                const [
                    contactRes,
                    footerRes,
                    ,
                    trainerRes,
                ] = await Promise.all([
                    getContact(),
                    getFooter(),
                    getNavigation(),
                    getTrainer(),
                    getHome(),
                    getPostural(),
                    getManagement(),
                    getCertification(),
                    getTeam(),
                    getGallery(),
                ]);

                setContact(contactRes || []);
                setFooter(footerRes || []);

                // Preload trainer banner image into browser cache while loading screen is showing
                if (trainerRes?.datas?.[0]?.value && typeof window !== "undefined") {
                    await new Promise((resolve) => {
                        const img = new Image();
                        img.src = process.env.REACT_APP_IMAGE_URL + trainerRes.datas[0].value;
                        img.onload = resolve;
                        img.onerror = resolve;
                        setTimeout(resolve, 1500); // 1.5s safety timeout
                    });
                }
            } catch (error) {
                console.error("Error fetching layout data:", error);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Full screen loading animation displayed on initial visit & any page refresh
    if (initialLoading) {
        return <Loading />;
    }

    return (
        <div className="flex flex-col w-full min-h-screen bg-[#08090d] text-white relative overflow-x-hidden">
            {/* Clean, Non-Blur Ambient Glow Accents (100% Zero Blur / Zero Filter for Silky 60fps) */}
            <div className="fixed -top-[20vw] -right-[15vw] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full bg-[radial-gradient(circle,rgba(234,42,94,0.08)_0%,transparent_70%)] pointer-events-none z-0" />
            <div className="fixed -bottom-[20vw] -left-[15vw] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full bg-[radial-gradient(circle,rgba(11,180,192,0.06)_0%,transparent_70%)] pointer-events-none z-0" />

            {/* Header */}
            <Header />

            {/* Main Page Content */}
            <main className="relative z-10 w-full flex-1">
                <Outlet />
            </main>

            {/* Footer */}
            <Footer contact={contact} footer={footer} />

            {/* Floating WhatsApp Action Button (Zero Blur, Zero Shadow Animation, Pure Linear Scale/Opacity) */}
            <a
                href={`https://wa.me/${waNumber}?text=${encodeURIComponent(textForWA)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-6 md:bottom-8 right-6 md:right-8 z-50 block"
                onMouseEnter={() => setShowNumber(true)}
                onMouseLeave={() => setShowNumber(false)}
            >
                <motion.div
                    layout
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                >
                    <div className="flex items-center gap-3 bg-gradient-to-r from-pink to-ungu border border-white/20 py-2.5 px-4 rounded-full cursor-pointer shadow-lg shadow-pink/10">
                        <div className="relative w-8 h-8 shrink-0">
                            <img
                                src="/icon/whatsapp.png"
                                alt="WhatsApp"
                                className="w-full h-full object-contain"
                            />
                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-[#00E676] rounded-full border-2 border-[#08090d]" />
                        </div>

                        <div className="flex flex-col pr-1 min-w-[85px]">
                            <div className="relative h-[18px] sm:h-[20px] flex items-center overflow-hidden">
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.span
                                        key={showNumber ? "number" : "chat"}
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -5 }}
                                        transition={{ duration: 0.2, ease: "easeInOut" }}
                                        className="text-xs md:text-sm font-extrabold tracking-wide text-white whitespace-nowrap leading-tight block"
                                    >
                                        {!showNumber ? "Chat with Us" : formattedNumber}
                                    </motion.span>
                                </AnimatePresence>
                            </div>
                            <span className="text-[10px] text-white/80 font-bold tracking-wider uppercase leading-tight">
                                I-Fit Specialist
                            </span>
                        </div>
                    </div>
                </motion.div>
            </a>
        </div>
    );
}
